/**
 * Semeia o evento de demonstração no banco local.
 *
 * Escreve nas mesmas tabelas que o sync escreve, com os mesmos formatos — é o
 * que faz as telas seguintes não saberem que estão em demonstração. Nenhuma
 * delas ganha um `if`: elas leem o banco como sempre.
 *
 * O critério de separação está em `src/dominio/demonstracao.ts`: id negativo.
 * Ver lá o porquê.
 */

import {banco} from './banco';
import {
  CODIGOS_DEMONSTRACAO,
  EVENTO_DEMONSTRACAO,
  LOTE_DEMONSTRACAO,
  NOME_EVENTO_DEMONSTRACAO,
  PORTADORES_DEMONSTRACAO,
  SETOR_DEMONSTRACAO,
} from '../dominio/demonstracao';

/**
 * Apaga e recria o evento de exemplo.
 *
 * Recriar, e não só inserir se faltar: quem entra em demonstração espera
 * começar do zero. Sem isto, o segundo login mostraria os ingressos que a
 * demonstração anterior já validou, e a primeira leitura diria JÁ VALIDADO —
 * exatamente o oposto do que se quer mostrar.
 */
export async function semearDemonstracao(): Promise<void> {
  const db = banco();

  await limparDemonstracao();

  await db.execute(
    'insert or replace into evento (id, nome, data, local, cidade) values (?, ?, ?, ?, ?)',
    [
      EVENTO_DEMONSTRACAO,
      NOME_EVENTO_DEMONSTRACAO,
      dataDeHoje(),
      'Local de exemplo',
      'Cidade de exemplo',
    ],
  );

  await db.execute(
    'insert into lote (eventoId, loteStr, setor, setorId, consumacao) values (?, ?, ?, ?, ?) ' +
      'on conflict(loteStr) do update set eventoId = excluded.eventoId, setor = excluded.setor, ' +
      'setorId = excluded.setorId, consumacao = excluded.consumacao',
    [EVENTO_DEMONSTRACAO, LOTE_DEMONSTRACAO, 'Pista', SETOR_DEMONSTRACAO, '0'],
  );

  // Habilitado, ao contrário do que o sync faz com setor novo. Ali o padrão é
  // desligado de propósito, para nenhuma portaria começar aceitando setor que
  // o operador não abriu. Aqui o objetivo é o oposto: quem entra tem que
  // conseguir validar sem descobrir antes que precisa ligar um interruptor.
  await db.execute(
    'insert or replace into setor (remoteId, eventoId, nome, habilitado) values (?, ?, ?, 1)',
    [SETOR_DEMONSTRACAO, EVENTO_DEMONSTRACAO, 'Pista'],
  );

  for (const portador of PORTADORES_DEMONSTRACAO) {
    await db.execute(
      'insert or replace into ingresso_online ' +
        '(eventoId, codbar, nome, email, cpf, pedidoId, status, setor, setorId, numero) ' +
        "values (?, ?, ?, ?, ?, ?, 'A', 'Pista', ?, ?)",
      [
        EVENTO_DEMONSTRACAO,
        portador.codbar,
        portador.nome,
        `${portador.nome.split(' ')[0].toLowerCase()}@exemplo.com`,
        portador.cpf,
        'PEDIDO-EXEMPLO',
        SETOR_DEMONSTRACAO,
        String(CODIGOS_DEMONSTRACAO.indexOf(portador.codbar) + 1),
      ],
    );
  }
}

/**
 * Tira do banco tudo que é demonstração, validações incluídas.
 *
 * As validações vão junto, e é a única exceção à regra de nunca apagar
 * `validado`: aquela regra existe porque validação de portaria não existe em
 * nenhum outro lugar. Esta existe só aqui, nunca foi enviada e nunca será —
 * guardá-la só faria a próxima demonstração começar suja.
 */
export async function limparDemonstracao(): Promise<void> {
  const db = banco();
  for (const [tabela, coluna] of [
    ['ingresso_online', 'eventoId'],
    ['ingresso_cancelado', 'eventoId'],
    ['lote', 'eventoId'],
    ['setor', 'eventoId'],
    ['sync_estado', 'eventoId'],
    ['validado', 'eventoId'],
    ['evento', 'id'],
  ] as const) {
    await db.execute(`delete from ${tabela} where ${coluna} < 0`);
  }
}

/** `evento.data` é texto no formato que a tela de eventos já sabe ler. */
function dataDeHoje(): string {
  const hoje = new Date();
  const dois = (n: number) => String(n).padStart(2, '0');
  return `${hoje.getFullYear()}-${dois(hoje.getMonth() + 1)}-${dois(hoje.getDate())}`;
}
